import { CustomAppApiAuthProvider } from './CustomAppApiAuthProvider';
import { BaseApiProvider } from './BaseApiProvider';
import { HttpMethod } from '../common/Enums';
import { Constants } from '../common/Constants';

export interface IItemPermission {
  id: string;
  user_principal_name?: string;
  group_name?: string;
  roles: string[];
}

export interface IItemPermissionsResponse {
  permissions: IItemPermission[];
}

export class ItemPermissionsProvider extends BaseApiProvider {
  public readonly apiUrl: string = Constants.VITE_APP_API_URL;
  public static readonly instance: ItemPermissionsProvider =
    new ItemPermissionsProvider();

  private constructor() {
    super(CustomAppApiAuthProvider.instance);
  }

  /**
   * Get permissions for an item
   * @param containerId The container ID
   * @param itemId The ID of the item
   * @returns List of permissions for the item
   */
  public async getPermissions(
    containerId: string,
    itemId: string,
  ): Promise<IItemPermission[]> {
    const token = await this.authProvider.getToken();
    const request = await this.createRequestInit(token, HttpMethod.GET);

    const response: IItemPermissionsResponse = await this._send(
      this.apiUrl,
      `/containers/${encodeURIComponent(containerId)}/items/${itemId}/permissions`,
      request,
    );
    return response.permissions;
  }

  /**
   * Add a permission to an item
   * @param containerId The container ID
   * @param itemId The ID of the item
   * @param principalName The user principal name or group name to grant the permission to
   * @param role The role to grant (must be 'read' or 'write')
   * @param isGroup Whether the principal is a group (default: false for user)
   * @returns The created permission
   */
  public async addPermission(
    containerId: string,
    itemId: string,
    principalName: string,
    role: string,
    isGroup: boolean = false,
  ): Promise<IItemPermission> {
    const token = await this.authProvider.getToken();
    const body: any = {
      role: role,
    };

    // Use user_principal_name for users or group_name for groups (mutually exclusive)
    if (isGroup) {
      body.group_name = principalName;
    } else {
      body.user_principal_name = principalName;
    }

    const request = await this.createRequestInit(token, HttpMethod.POST, body);

    return await this._send(
      this.apiUrl,
      `/containers/${encodeURIComponent(containerId)}/items/${itemId}/permissions`,
      request,
    );
  }

  /**
   * Delete a permission from an item
   * @param containerId The container ID
   * @param itemId The ID of the item
   * @param permissionId The ID of the permission to delete
   */
  public async deletePermission(
    containerId: string,
    itemId: string,
    permissionId: string,
  ): Promise<void> {
    const token = await this.authProvider.getToken();
    const request = await this.createRequestInit(token, HttpMethod.DELETE);

    await this._send(
      this.apiUrl,
      `/containers/${encodeURIComponent(containerId)}/items/${itemId}/permissions/${permissionId}`,
      request,
    );
  }
}
